import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TS types
import { RootState } from '@redux/store';
import { ITicketBlockProps, IETItem, IPlanList } from '@redux/common';
import { ITHSRStore } from '@redux/THSR/interfaces';

// shared component
import Counter from '@component/Common/counter';
import Lock from '@svg/Lock'; // svg component

// common function
import { addSeparator, MapItems, calTicketLowQty, getSessionInfo } from '@libs/core';

// redux actions
import { setTicketErrorMsg, setTicketNum } from '@redux/Normal/actions';

// plugin
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

const TicketBlock = React.forwardRef<HTMLDivElement, ITicketBlockProps>((props, ref?) => {
    const { isCrossell } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { 
        DetailData, 
        pickCalendar, 
        ticketNum, 
        NowListData, 
        ticketErrorMsg, 
        allSessions, 
        pickSession,
        isSessionLoading 
    } = useSelector((state: RootState) => state.Normal);
    const { ListData, isLogin, isErpOrder } = useSelector((state: RootState) => state.Common);
    const { THSRInitData }: ITHSRStore = useSelector((state: RootState) => state.THSR);
    const { IsPickOneEtitem, SaleCurrency }: IPlanList = ListData; 
    const hasCalendar = NowListData?.ExpectedUseDay;

    const saleCurrency = SaleCurrency;
    const isSoldOut = (item: IETItem) => {
        if (!item.IsForSale || item.AllowQty === 0 || item.AllowQty < item.LowQty) return true; // 後台補貨中打勾 或 庫存為0 或 庫存小於最低應夠數量
        return false;
    };

    // 設單品是否該disable 增加按鈕功能
    const isDisableAdd = (obj: { [keys: string]: number }, itemSeq: string) => {
        if (!IsPickOneEtitem) return false;
        const selectedItem: string[] = [];
        for (let el in obj) {
            if (obj[el] > 0) {
                selectedItem.push(el);
            }
        }
        return selectedItem.length > 0 && selectedItem.indexOf(itemSeq) === -1;
    };

    const [ETdata, setETdata] = React.useState<IETItem[]>(null);

    // ETdata
    useEffect(() => {
        if (!DetailData) return;
        if (!hasCalendar) { // 沒月曆、沒&有場次
            setETdata(DetailData.ETItem);
            return;
        }
        if (!DetailData.Sessions) { // 有月曆、沒場次
            Object.keys(DetailData.CalendarDate).length !== 0
                ? setETdata(DetailData.CalendarDate[pickCalendar]?.ETItem)
                : setETdata(DetailData.ETItem);
            return;
        }
        if (allSessions && pickSession) { // 有月曆、有場次
            // setETdata(getSessionInfo({ session, pickSession })?.ETItem); TODO: 第三階段處理
            Object.keys(DetailData.CalendarDate).length !== 0 // TODO: 第一階段暫用 
                ? setETdata(DetailData.CalendarDate[pickCalendar]?.ETItem)
                : setETdata(DetailData.ETItem); 
        } 
    }, [pickSession, DetailData, pickCalendar, ticketNum, allSessions]);

    // TODO: setTTicketNum 第三階段處理 [事件1]
    // useEffect(() => {
    //     if (!allSessions || !pickSession) return;
    //     // dispatch(
    //     //     setTicketNum(convertTicketNum(getSessionInfo({ allSessions, pickSession })?.ETItem))
    //     // );
    // }, [pickSession?.SessionUUID]);

    // const _ETItemData = useMemo(() => {
    //     if (!THSRInitData?.HasTHSR) return ETItemData;
    //     return MapItems({ listItems: ETItemData });
    // }, [ETItemData, THSRInitData?.HasTHSR]);

    return (
        <div className='ticketBlock' ref={ref}>

            <div className="ticketTitleWrap">
                <div className="subTitle">{t('noTHSR.ticket.ticketAndNumber')}</div>
                <div className={cx('errorMsg', { showError: !!ticketErrorMsg })}>
                    {ticketErrorMsg}
                </div>
            </div>
            <div className={cx('tickets', { load_hide: isSessionLoading })}>
                {ETdata && ETdata.map((item: IETItem) => {
                    return (
                        <div
                            className={cx('ticket', {
                                soldout: isSoldOut(item),
                                hasmembertag: item.SalePrice > 0 && !isLogin
                            })}
                            key={item.ItemSeq}
                        >
                            <div className="ticketTitle">{item.Name}</div>
                            <div className="ticketPrice">
                                <div className="priceWrap">
                                    <div className='normalPriceContent'>
                                        {/* 原價 */}
                                        {!!item?.ListPrice &&
                                            item.ListPrice > 0 &&
                                            item.ListPrice > item.SalePrice_NonMember && (
                                                <span className="price originalPrice">
                                                    {`${saleCurrency} ${addSeparator(item.ListPrice)}`}
                                                </span>
                                            )
                                        }
                                        {/* 售價&會員價 */}
                                        <span className="price">
                                            { isCrossell && item.SalePrice_Spread > 0 &&
                                                <span className='originalPrice'>
                                                    {saleCurrency} {addSeparator(item.SalePrice_NonMember)}
                                                </span>
                                            }
                                            {/* 有電話單 就直接顯示會員價(會員價 !== 0 ) */}
                                            {/* 如果有登入 + 會員價 !== 0 就將售價改成顯示會員價 */}
                                            {
                                                !isErpOrder 
                                                ? ( isLogin
                                                    ? item.SalePrice > 0
                                                        ? isCrossell && item.SalePrice_Spread > 0
                                                            ? `${saleCurrency} ${addSeparator(item.SalePrice_NonMember - item.SalePrice_Spread)}`
                                                            : `${saleCurrency} ${addSeparator(item.SalePrice)}`
                                                        : `${saleCurrency} ${addSeparator(item.SalePrice_NonMember)}`
                                                    : `${saleCurrency} ${addSeparator(item.SalePrice_NonMember)}`
                                                ) : (
                                                    item.SalePrice > 0
                                                        ? `${saleCurrency} ${addSeparator(item.SalePrice)}`
                                                        : `${saleCurrency} ${addSeparator(item.SalePrice_NonMember)}`
                                                )
                                            }
                                        </span>
                                    </div>
                                    {/* 會員價lock版 */}
                                    {!isErpOrder && !isLogin && item.SalePrice > 0 && (
                                        <div className='memberPriceContent'>
                                            <Lock/>
                                            <span className="price memberPrice">
                                                {`會員價 ${addSeparator(item.SalePrice)}`}
                                            </span>
                                        </div>
                                        )
                                    }
                                </div>
                                {isSoldOut(item) ? (
                                    <div className="soldoutText">
                                        {item.NotForSaleReason}
                                    </div>
                                ) : (
                                    <Counter
                                        ticketSeq={item.ItemSeq}
                                        lowQty={
                                            calTicketLowQty(item)
                                        }
                                        maxNum={
                                            pickCalendar || pickSession
                                                ? Math.min(item.HighQty, item.AllowQty)
                                                : item.HighQty
                                        }
                                        defaultValue={ticketNum[item.ItemSeq]}
                                        onChange={(num: number) => {
                                            dispatch(
                                                setTicketNum({
                                                    ...ticketNum,
                                                    ...{ [item.ItemSeq]: num },
                                                })
                                            );
                                            ticketErrorMsg && dispatch(setTicketErrorMsg(''));
                                        }}
                                        multiple={item.Mutiple}
                                        atLeastNum={item.StartBuyQty} //自建專用
                                        disableAdd={isDisableAdd(ticketNum, item.ItemSeq)}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});
export default TicketBlock;
